import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import { MapComponent } from "../../map/map.component";
import { ReplaySubject } from "rxjs/ReplaySubject";

const GEOLOCATION_ERRORS = {
  'errors.location.unsupportedBrowser': 'Browser does not support location services',
  'errors.location.permissionDenied': 'You have rejected access to your location',
  'errors.location.positionUnavailable': 'Unable to determine your location',
  'errors.location.timeout': 'Service timeout has been reached'
};

interface Coordinates {
  lat: number,
  lng: number
}


@Injectable()
export class GeolocationService {

  private subject = new ReplaySubject<Position>();

  constructor() {
    this.getLocation().subscribe(this.subject);
  }

  /**
   * Obtains the geographic position, in terms of latitude and longitude coordinates, of the device.
   * @param {Object} [opts] An object literal to specify one or more of the following attributes and desired values:
   *   - enableHighAccuracy: Specify true to obtain the most accurate position possible, or false to optimize in favor of performance and power consumption.
   *   - timeout: An Integer value that indicates the time, in milliseconds, allowed for obtaining the position.
   *              If timeout is Infinity, (the default value) the location request will not time out.
   *              If timeout is zero (0) or negative, the results depend on the behavior of the location provider.
   *   - maximumAge: An Integer value indicating the maximum age, in milliseconds, of cached position information.
   *                 If maximumAge is non-zero, and a cached position that is no older than maximumAge is available, the cached position is used instead of obtaining an updated location.
   *                 If maximumAge is zero (0), watchPosition always tries to obtain an updated position, even if a cached position is already available.
   *                 If maximumAge is Infinity, any cached position is used, regardless of its age, and watchPosition only tries to obtain an updated position if no cached position data exists.
   * @returns {Observable} An observable sequence with the geographical location of the device running the client.
   */
  public getLocation(opts: PositionOptions = {}): Observable<Position> {

    return Observable.create(observer => {
      let watchId;
      if (window.navigator && window.navigator.geolocation) {
        watchId = window.navigator.geolocation.watchPosition(
          (position) => {
            observer.next(position);
          },
          (error) => {
            switch (error.code) {
              case 1:
                observer.error(GEOLOCATION_ERRORS['errors.location.permissionDenied']);
                break;
              case 2:
                observer.error(GEOLOCATION_ERRORS['errors.location.positionUnavailable']);
                break;
              case 3:
                observer.error(GEOLOCATION_ERRORS['errors.location.timeout']);
                break;
            }
          },
          opts);
      }
      else {
        observer.error(GEOLOCATION_ERRORS['errors.location.unsupportedBrowser']);
      }

      return () => window.navigator.geolocation.clearWatch(watchId);

    });

  }

  public get location() {
    return this.subject.asObservable();
  }

  private static rad2degr(rad) {
    return rad * 180 / Math.PI;
  }

  private static degr2rad(degr) {
    return degr * Math.PI / 180;
  }

  public static distance(a: Coordinates, b: Coordinates) {
    const earthRadius = 6371e3;
    let latA = GeolocationService.degr2rad(a.lat);
    let latB = GeolocationService.degr2rad(b.lat);
    let deltaLat = GeolocationService.degr2rad(a.lat - b.lat);
    let deltaLng = GeolocationService.degr2rad(a.lng - b.lng);

    let tmp = Math.pow(Math.sin(deltaLat / 2), 2) + Math.cos(latA) * Math.cos(latB) * Math.pow(Math.sin(deltaLng / 2), 2)
    let c = 2 * Math.atan2(Math.sqrt(tmp), Math.sqrt(1 - tmp));

    return earthRadius * c;

  }



}



// WEBPACK FOOTER //
// /home/markush/Projects/fieldsale-wingman-client/src/app/shared/services/geo-location.service.ts